import { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '../store'
import { AUDIO_PVE_URL } from '../constants/audioAssets'
import { playExclusiveBackgroundMusic, releaseBackgroundMusic } from '../common/backgroundMusicBus'

// Global audio instance to persist across page navigations
let globalPVEAudio: HTMLAudioElement | null = null
let hasUserInteracted = false

export const usePVEAudio = () => {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const backgroundMusicEnabled = useSelector((state: RootState) => state.app.settings.backgroundMusicEnabled)
  const musicVolume = useSelector((state: RootState) => state.app.settings.musicVolume)

  useEffect(() => {
    // Initialize global audio instance if not already created
    if (!globalPVEAudio && typeof window !== 'undefined') {
      globalPVEAudio = new Audio(AUDIO_PVE_URL)
      globalPVEAudio.loop = true
      globalPVEAudio.preload = 'auto'
      globalPVEAudio.volume = musicVolume
    }

    // Set the ref to the global audio instance
    if (globalPVEAudio) {
      audioRef.current = globalPVEAudio
      // Update volume when it changes
      globalPVEAudio.volume = musicVolume
    }

    const playAudio = async () => {
      if (!globalPVEAudio) return

      try {
        // If audio is already playing, don't restart it
        if (!globalPVEAudio.paused) {
          return
        }

        if (globalPVEAudio.readyState < 2) {
          await new Promise((resolve) => {
            if (globalPVEAudio) {
              globalPVEAudio.addEventListener('canplaythrough', resolve, { once: true })
              globalPVEAudio.load()
            }
          })
        }

        if (backgroundMusicEnabled) {
          await playExclusiveBackgroundMusic(globalPVEAudio).catch((error) => {
          })
        } else {
          globalPVEAudio.pause()
          releaseBackgroundMusic(globalPVEAudio)
        }
      } catch (error) {
        console.error('PVE Audio error:', error)
      }
    }

    const handleUserInteraction = () => {
      hasUserInteracted = true
      playAudio()
    }

    // Only add listeners if user hasn't interacted yet and audio is paused
    if (!hasUserInteracted && (!globalPVEAudio || globalPVEAudio.paused)) {
      document.addEventListener('click', handleUserInteraction, { once: true })
      document.addEventListener('keydown', handleUserInteraction, { once: true })
      document.addEventListener('touchstart', handleUserInteraction, { once: true })
    }

    // Play/pause audio based on settings
    if (backgroundMusicEnabled) {
      if (hasUserInteracted && globalPVEAudio && globalPVEAudio.paused) {
        playAudio()
      }
    } else {
      if (globalPVEAudio && !globalPVEAudio.paused) {
        globalPVEAudio.pause()
        releaseBackgroundMusic(globalPVEAudio)
      }
    }

    return () => {
      document.removeEventListener('click', handleUserInteraction)
      document.removeEventListener('keydown', handleUserInteraction)
      document.removeEventListener('touchstart', handleUserInteraction)
    }
  }, [backgroundMusicEnabled, musicVolume])

  return audioRef
}

// Function to stop PVE audio (call this when leaving PVE section entirely)
export const stopPVEAudio = () => {
  if (globalPVEAudio) {
    globalPVEAudio.pause()
    globalPVEAudio.currentTime = 0
    releaseBackgroundMusic(globalPVEAudio)
    hasUserInteracted = false
  }
}

